import React, { useEffect, useState } from 'react';
import { Button, Empty, List, message, Modal, Popconfirm, Spin, Tooltip, Typography, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { FileText, Upload as UploadIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import type { ErpAttachment } from '../../../../api/FinanceApiClient';
import { downloadBlob } from '../../../../helpers/downloadBlob';
import { useStore } from '../../../../stores/root-store-context';
import { validateErpAttachment } from './erpAttachmentRules';

dayjs.extend(localizedFormat);

const { Dragger } = Upload;
const { Text, Link } = Typography;

/** Server codes from `ErpAttachmentService.UploadAsync` (via `CrmAttachmentRules.Validate`, which
 * ERP reuses unchanged - see `erpAttachmentRules.ts`) that have a dedicated, specific locale key.
 * `FileAlreadyExists` is handled separately (replace-confirm flow, below); any other/unknown code
 * falls back to the generic `FailedToUpload`. */
const UPLOAD_ERROR_CODES_WITH_MESSAGE = new Set(['FileTooLarge', 'FileTypeNotAllowed', 'FileEmpty', 'UploadFailed', 'ParentNotFound']);

function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function codeOf(error: unknown): string | undefined {
  return (error as { response?: { data?: { code?: string } } } | null | undefined)?.response?.data?.code;
}

function uploadedLabel(att: ErpAttachment): string {
  if (!att.uploadedAt) return '';
  const d = dayjs(att.uploadedAt);
  return d.isValid() ? d.format('LLL') : '';
}

export type OperationAttachmentsProps = {
  transactionId: number;
  readOnly?: boolean;
};

/**
 * Attachments block on the Operation (FinancialTransaction) edit modal. Mounted only once the
 * transaction has an id (edit mode - see `TransactionManagement.openEditModal` vs `openAddModal`).
 * Mirrors the Kanban `TaskDetails/TaskAttachments` structure (Dragger + list, client-side size
 * gate, replace-on-409) against the ERP `FinanceStore` actions from Task 5, minus the inline-image
 * specifics: ERP attachments are plain files only, with no rich-text surface to embed into.
 *
 * List/loading state is kept locally rather than read from `financeStore.erpAttachments`/
 * `isLoading`: those are shared across every FinanceStore action (including this same modal's own
 * Save), so reading them here would (a) briefly show a stale attachment list left over from
 * whichever transaction was viewed previously, for the instant between this mount and its own
 * fetch resolving, and (b) flicker this section on every unrelated store call. Local state
 * sidesteps both; the store's own `erpAttachments`/`isLoading` are still updated by the actions
 * below (other consumers may rely on them), this component just doesn't read them back.
 *
 * `getErpAttachmentDownloadUrl` names a JWT-`[Authorize]`'d server route (no query-string token
 * fallback outside the SignalR hubs - see `Program.cs`'s `OnMessageReceived`), so a bare
 * `<a href>`/`window.open` to it would 401. `handleDownload` fetches it with the bearer header
 * attached (same `apiStore.configApi.token` read `TicketChatStore` uses for its own out-of-band
 * auth) and saves the returned blob.
 */
const OperationAttachments: React.FC<OperationAttachmentsProps> = observer(({ transactionId, readOnly = false }) => {
  const { t } = useTranslation();
  const { financeStore } = useStore();
  const [attachments, setAttachments] = useState<ErpAttachment[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setAttachments([]);
    setLoadingList(true);
    financeStore
      .getErpAttachments(transactionId)
      .then(list => {
        if (!cancelled) setAttachments(list);
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  /** Server has no confirm-overwrite flag the way the Kanban upload route does (a same-named file
   * always 409s - see `ErpAttachmentService.UploadAsync`'s unconditional `FileAlreadyExists`
   * check). "Replace" is therefore implemented client-side: delete the existing row, then upload. */
  const replaceExisting = async (file: File): Promise<boolean> => {
    const match = attachments.find(a => a.fileName.toLowerCase() === file.name.toLowerCase());
    if (match) {
      const deleted = await financeStore.deleteErpAttachment(match.id);
      if (!deleted) return false;
      setAttachments(prev => prev.filter(a => a.id !== match.id));
    }
    return attemptUpload(file, true);
  };

  const attemptUpload = async (file: File, isReplaceRetry: boolean): Promise<boolean> => {
    try {
      const attachment = await financeStore.uploadErpAttachment(transactionId, file);
      setAttachments(prev => [attachment, ...prev.filter(a => a.id !== attachment.id)]);
      message.success(t('AttachmentUploaded', { name: file.name }));
      return true;
    } catch (error) {
      // Branch on the server's `code`, not the HTTP status: 409/FileAlreadyExists offers Replace;
      // the other known codes each get their own specific message; anything else (including no
      // code at all, e.g. a network error) falls back to the generic FailedToUpload. The client
      // gate in beforeUpload already catches FileEmpty/FileTypeNotAllowed/FileTooLarge before a
      // request is even sent, but this mapping is defense-in-depth for whatever reaches the server
      // regardless (a second tab's stale rules, a direct API caller, a future gate bug).
      const code = codeOf(error);
      if (!isReplaceRetry && code === 'FileAlreadyExists') {
        return new Promise<boolean>(resolve => {
          Modal.confirm({
            title: t('FileAlreadyExists'),
            okText: t('Replace'),
            cancelText: t('Cancel'),
            onOk: async () => resolve(await replaceExisting(file)),
            onCancel: () => resolve(false),
          });
        });
      }
      console.error('Error uploading ERP attachment:', error);
      message.error(code && UPLOAD_ERROR_CODES_WITH_MESSAGE.has(code) ? t(code) : t('FailedToUpload'));
      return false;
    }
  };

  const customRequest: UploadProps['customRequest'] = async ({ file, onSuccess, onError }: any) => {
    setUploading(true);
    try {
      const ok = await attemptUpload(file as File, false);
      if (ok) {
        onSuccess?.({}, new XMLHttpRequest());
      } else {
        onError?.(new Error('Upload failed'));
      }
    } catch (error) {
      onError?.(error as Error);
    } finally {
      setUploading(false);
    }
  };

  const handleChange: UploadProps['onChange'] = info => {
    if (info.file.status === 'error') {
      message.error(`${info.file.name} upload failed.`);
    }
    setFileList(info.fileList.slice(-5));
  };

  const handleDelete = async (id: number) => {
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      const success = await financeStore.deleteErpAttachment(id);
      if (success) {
        setAttachments(prev => prev.filter(a => a.id !== id));
        message.success(t('AttachmentDeleted'));
      } else {
        message.error(t('FailedToDeleteAttachment'));
      }
    } catch (error) {
      console.error('Error deleting ERP attachment:', error);
      message.error(t('FailedToDeleteAttachment'));
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleDownload = async (att: ErpAttachment) => {
    try {
      const token = financeStore.apiStore.configApi.token;
      const response = await fetch(financeStore.getErpAttachmentDownloadUrl(att.id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      downloadBlob(await response.blob(), att.fileName);
    } catch (error) {
      console.error('Error downloading ERP attachment:', error);
      message.error(t('FailedToDownloadAttachment'));
    }
  };

  return (
    <section style={{ marginTop: 16 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {t('Attachments')}
      </Text>

      {!readOnly && (
        <Spin spinning={uploading}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            {t('UploadFile')}
          </Text>
          <Dragger
            name="file"
            fileList={fileList}
            onChange={handleChange}
            customRequest={customRequest}
            showUploadList={{ showRemoveIcon: true }}
            multiple={false}
            beforeUpload={file => {
              const error = validateErpAttachment(file);
              if (error) {
                message.error(t(error));
                return Upload.LIST_IGNORE;
              }
              return true;
            }}
          >
            <p className="ant-upload-drag-icon">
              <UploadIcon size={32} />
            </p>
            <p className="ant-upload-text">{t('ClickOrDragFileToUpload')}</p>
            <p className="ant-upload-hint">{t('SupportForSingleOrBulkUpload')}</p>
          </Dragger>
        </Spin>
      )}

      <List
        style={{ marginTop: 16 }}
        loading={loadingList}
        dataSource={attachments}
        locale={{ emptyText: <Empty description={t('NoAttachmentsYet')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        renderItem={att => {
          const uploaded = uploadedLabel(att);
          const by = att.uploadedByName;
          const description = [formatAttachmentSize(att.sizeBytes), uploaded, by ? `${t('UploadedBy')}: ${by}` : '']
            .filter(Boolean)
            .join(' · ');
          // Рядок — вертикальний: назва на всю ширину рядка, кнопки під нею.
          // Без `actions` у List.Item: бокові actions забирають ширину в контенту
          // і вузька назва знову розтягується по вертикалі.
          return (
            <List.Item key={att.id} style={{ display: 'block', padding: '12px 0' }}>
              <Spin spinning={deletingIds.has(att.id)} size="small">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
                  <FileText size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={att.fileName}>
                      <Link
                        onClick={() => handleDownload(att)}
                        ellipsis
                        style={{
                          display: 'block',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {att.fileName}
                      </Link>
                    </Tooltip>
                    <Tooltip title={description}>
                      <Text
                        type="secondary"
                        style={{
                          display: 'block',
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {description}
                      </Text>
                    </Tooltip>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 8, marginLeft: 28 }}>
                  <Button type="link" size="small" onClick={() => handleDownload(att)} style={{ paddingLeft: 0 }}>
                    {t('View')}
                  </Button>
                  {!readOnly && (
                    <Popconfirm
                      title={t('ConfirmDeleteAttachment')}
                      description={t('AreYouSureDeleteAttachment')}
                      onConfirm={() => handleDelete(att.id)}
                      okText={t('Delete')}
                      cancelText={t('Cancel')}
                      okButtonProps={{ danger: true }}
                    >
                      <Button type="link" danger size="small">
                        {t('Delete')}
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              </Spin>
            </List.Item>
          );
        }}
      />
    </section>
  );
});

export default OperationAttachments;
